import { useCallback } from 'react';
import { computePositionRect, POSITION_TRANSITION, type PositionInput, useChatWidgetPositionStore } from './store';
import useElement from './useElement';

const setElementPosition = (el: HTMLElement | null, position: PositionInput, isFooterVisible: boolean) => {
  if (!el) return;
  const { right, bottom } = computePositionRect(position, isFooterVisible);
  el.style.transition = POSITION_TRANSITION;
  el.style.setProperty('right', `${right}px`, 'important');
  el.style.setProperty('bottom', `${bottom}px`, 'important');
};

const useChatWidget = () => {
  const { element: hubspotElement } = useElement('#hubspot-messages-iframe-container');
  const { element: blipChatElement } = useElement('#blip-chat-container #blip-chat-open-iframe', { observe: true });
  const { isVisible: isFooterVisible } = useElement('#page-footer', { trackVisibility: true });
  const setStorePosition = useChatWidgetPositionStore((s) => s.setPosition);

  const setPosition = useCallback(
    (position: PositionInput) => {
      setElementPosition(hubspotElement, position, isFooterVisible);
      setElementPosition(blipChatElement, position, isFooterVisible);
      setStorePosition(position);
    },
    [hubspotElement, blipChatElement, isFooterVisible, setStorePosition]
  );

  const hide = useCallback(
    (hideIt: boolean) => {
      if (hubspotElement) {
        hubspotElement.style.setProperty('display', hideIt ? 'none' : 'initial', 'important');
      }
      if (blipChatElement) {
        blipChatElement.style.setProperty('display', hideIt ? 'none' : 'initial', 'important');
      }
    },
    [hubspotElement, blipChatElement]
  );

  const openBlipChat = useCallback(() => {
    if (window.blipChatInstance?.toogleChat) {
      window.blipChatInstance.toogleChat();
      return;
    }

    if (window.blipChatBuilder?.toogleChat) {
      window.blipChatBuilder.toogleChat();
    }
  }, []);

  const sendBlipChatMessage = useCallback((message: string) => {
    if (window.blipChatInstance?.sendMessage) {
      window.blipChatInstance.sendMessage({
        type: 'text/plain',
        content: message,
      });
      return;
    }

    if (window.blipChatBuilder?.sendMessage) {
      window.blipChatBuilder.sendMessage({
        type: 'text/plain',
        content: message,
      });
    }
  }, []);

  return {
    setPosition,
    hide,
    openBlipChat,
    sendBlipChatMessage,
    hubspotElement,
    blipChatElement,
    isFooterVisible,
  };
};

export default useChatWidget;
